import React, { useState } from 'react'
import { Modal } from '../Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Col, Container, Form, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { Divider } from '../Divider/Divider'
import { Button } from '../Button/Button'
import {
  removeEmailFilter,
  resetCustomEmailFilter,
  selectCustomEmailFilter,
  selectDefaultEmailFilter,
  selectEditingQuickEmailFilter,
  selectEmailFilterAfter,
  selectEmailFilterBefore,
  selectEmailFilterEmailTypeIds,
  selectEmailFilterModalMode,
  selectEmailFilterSender,
  selectEmailFilterTags,
  selectSelectedQuickEmailFilter,
  selectShowEmailFilterModal,
  setEmailFilter,
  setEmailFilterAfter,
  setEmailFilterBefore,
  setEmailFilterEmailTypeIds,
  setEmailFilterModalMode,
  setEmailFilterTags,
  setSelectedQuickEmailFilter,
  setShowEmailFilterModal,
} from '../../store/emailHub/emailFilters'
import { EmailContactFilter } from './EmailContactFilter'
import { EmailDateFilter } from './EmailDateFilter'
import { EmailTagsFilter } from './EmailTagsFilter'
import { EmailTypeFilter } from './EmailTypeFilter'
import { useCreateEmailFilterMutation, useDeleteEmailFilterMutation, useUpdateEmailFilterMutation } from '../../store/integrations/emailFilters'
import { useFetchEmailThreads } from '../../views/EmailV2/hooks'
import { selectAccountId } from '../../store/auth/authSlice'

const _MIN_NAME_LENGTH = 3

export const EmailFilterModal: React.FC = () => {
  const dispatch = useAppDispatch()

  const showEmailFilterModal = useAppSelector(selectShowEmailFilterModal)

  const hideEmailFilterModal = () => {
    dispatch(setShowEmailFilterModal(false))
  }

  if (!showEmailFilterModal) {
    return null
  }

  return <Modal header={<></>} body={<EmailFilterModalBody />} show={showEmailFilterModal} onHide={hideEmailFilterModal} footer={<></>} />
}

const EmailFilterModalBody: React.FC = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const defaultEmailFilter = useAppSelector(selectDefaultEmailFilter)
  const modalMode = useAppSelector(selectEmailFilterModalMode)
  const editingQuickEmailFilter = useAppSelector(selectEditingQuickEmailFilter)
  const customEmailFilter = useAppSelector(selectCustomEmailFilter)
  const emailFilterSender = useAppSelector(selectEmailFilterSender)
  const emailFilterAfter = useAppSelector(selectEmailFilterAfter)
  const emailFilterBefore = useAppSelector(selectEmailFilterBefore)
  const emailFilterEmailTypeIds = useAppSelector(selectEmailFilterEmailTypeIds)
  const emailFilterTags = useAppSelector(selectEmailFilterTags)

  const [triggerCreateEmailFilter, { isLoading: createFilterLoading }] = useCreateEmailFilterMutation()
  const [triggerUpdateEmailFilter, { isLoading: updateFilterLoading }] = useUpdateEmailFilterMutation()
  const [triggerDeleteEmailFilter, { isLoading: deleteFilterLoading }] = useDeleteEmailFilterMutation()

  const [name, setName] = useState<string>(modalMode === 'edit' ? (editingQuickEmailFilter?.name ?? '') : '')
  const [isDefault, setDefault] = useState<boolean>(modalMode === 'edit' ? (editingQuickEmailFilter?.isDefault ?? false) : false)

  const { fetchEmailThreads } = useFetchEmailThreads()

  const setModalMode = (modalMode: 'view' | 'edit' | 'new') => {
    dispatch(setEmailFilterModalMode(modalMode))
  }

  const resetDateRangeFilter = () => {
    dispatch(setEmailFilterAfter(null))
    dispatch(setEmailFilterBefore(null))
  }

  const resetEmailTypeIds = () => {
    dispatch(setEmailFilterEmailTypeIds([]))
  }

  const resetTags = () => {
    dispatch(setEmailFilterTags([]))
  }

  const resetFilter = () => {
    dispatch(resetCustomEmailFilter())
  }

  const handleShowResults = async () => {
    if (!editingQuickEmailFilter && !customEmailFilter) {
      dispatch(setShowEmailFilterModal(false))
      return
    }
    const filterParams = editingQuickEmailFilter ? { filterId: editingQuickEmailFilter.id } : { filter: customEmailFilter }
    await fetchEmailThreads(filterParams)
    dispatch(setShowEmailFilterModal(false))
  }

  const handleSaveFilter = async () => {
    if (modalMode === 'edit') {
      await handleUpdateFilter()
    } else {
      await handleCreateFilter()
    }
  }

  const handleCreateFilter = async () => {
    if (!customEmailFilter) {
      return
    }
    try {
      const emailFilter = await triggerCreateEmailFilter({
        accountId,
        payload: {
          name,
          isDefault,
          filter: customEmailFilter,
        },
      }).unwrap()
      dispatch(setEmailFilterModalMode('view'))
      dispatch(setEmailFilter(emailFilter))
      dispatch(setSelectedQuickEmailFilter(emailFilter))
    } catch (e) {
      console.error(e)
    }
  }

  const handleUpdateFilter = async () => {
    if (!editingQuickEmailFilter || editingQuickEmailFilter.type === 'system' || !customEmailFilter) {
      return
    }
    try {
      const emailFilter = await triggerUpdateEmailFilter({
        accountId,
        emailFilterId: editingQuickEmailFilter.id,
        payload: {
          name,
          isDefault,
          filter: customEmailFilter,
        },
      }).unwrap()
      dispatch(setEmailFilterModalMode('view'))
      dispatch(setEmailFilter(emailFilter))
      dispatch(setSelectedQuickEmailFilter(emailFilter))
    } catch (e) {
      console.error(e)
    }
  }

  const handleDeleteFilter = async () => {
    if (!editingQuickEmailFilter || editingQuickEmailFilter.type === 'system') {
      return
    }
    try {
      await triggerDeleteEmailFilter({ accountId, emailFilterId: editingQuickEmailFilter.id }).unwrap()
      dispatch(removeEmailFilter(editingQuickEmailFilter.id))
      if (defaultEmailFilter) {
        dispatch(setSelectedQuickEmailFilter(defaultEmailFilter))
      }
    } catch (e) {
      console.error(e)
    }
    dispatch(setShowEmailFilterModal(false))
  }

  return (
    <Container>
      <Row className='gy-3'>
        <Col xs={12} className={classNames('zocks-border-0.25 p-2', { 'zocks-bg-color-gray-100': !!emailFilterSender })}>
          <EmailContactFilter />
        </Col>
        <Col xs={12} className={classNames('zocks-border-0.25 p-2', { 'zocks-bg-color-gray-100': emailFilterAfter && emailFilterBefore })}>
          <div className='d-flex flex-column gap-2'>
            <EmailDateFilter />
            {emailFilterAfter && emailFilterBefore ? (
              <div className='d-flex justify-content-end'>
                <div className='d-flex gap-1' role='button' onClick={resetDateRangeFilter}>
                  <i className='bi bi-arrow-clockwise' />
                  <div className='button-link-medium'>Clear</div>
                </div>
              </div>
            ) : null}
          </div>
        </Col>
        <Col xs={12} className={classNames('zocks-border-0.25 p-2', { 'zocks-bg-color-gray-100': emailFilterEmailTypeIds.length })}>
          <div className='d-flex flex-column gap-2'>
            <EmailTypeFilter />
            {emailFilterEmailTypeIds.length ? (
              <div className='d-flex justify-content-end'>
                <div className='d-flex gap-1' role='button' onClick={resetEmailTypeIds}>
                  <i className='bi bi-arrow-clockwise' />
                  <div className='button-link-medium'>Clear</div>
                </div>
              </div>
            ) : null}
          </div>
        </Col>
        <Col xs={12} className={classNames('zocks-border-0.25 p-2', { 'zocks-bg-color-gray-100': emailFilterTags.length })}>
          <div className='d-flex flex-column gap-2'>
            <>
              <EmailTagsFilter />
              {emailFilterTags.length ? (
                <div className='d-flex justify-content-end'>
                  <div className='d-flex gap-1' role='button' onClick={resetTags}>
                    <i className='bi bi-arrow-clockwise' />
                    <div className='button-link-medium'>Clear</div>
                  </div>
                </div>
              ) : null}
            </>
          </div>
        </Col>
      </Row>
      <Divider className='zocks-mt-1.25 zocks-mb-1.25' />
      {modalMode === 'edit' || modalMode === 'new' ? (
        <Row className='gy-3'>
          <Col xs={12}>
            <Form.Label>Choose name*</Form.Label>
            <Form.Control type='text' placeholder='Enter quick filter name' value={name} onChange={(e) => setName(e.target.value)} />
          </Col>
          <Col xs={12}>
            <Form.Check id='filter-is-default-check' type='checkbox'>
              <Form.Check.Input type='checkbox' checked={isDefault} onChange={(e) => setDefault(e.target.checked)} />
              <Form.Check.Label>
                <span className='paragraph-small'>Make Default</span>
              </Form.Check.Label>
            </Form.Check>
          </Col>
        </Row>
      ) : null}
      <Row className='gy-3 zocks-mt-2.0'>
        <Col sm={12} md className='d-flex align-items-center'>
          {modalMode === 'view' ? (
            <div role='button' className='d-flex align-items-center' onClick={() => setModalMode('edit')}>
              + Save as quick filter
            </div>
          ) : null}
          {modalMode === 'edit' ? (
            <div>
              <Button variant='danger' className='button-medium' onClick={handleDeleteFilter} loading={deleteFilterLoading}>
                <i className='bi bi-trash3' />
                Delete
              </Button>
            </div>
          ) : null}
        </Col>
        <Col sm={12} md='auto'>
          <div className='d-flex gap-2'>
            {modalMode === 'view' ? (
              <>
                <Button variant='secondary' className='flex-grow-1 button-medium' onClick={resetFilter}>
                  Clear all
                </Button>
                <Button variant='primary' className='flex-grow-1 button-medium' onClick={handleShowResults}>
                  Apply filter
                </Button>
              </>
            ) : (
              <>
                <Button variant='secondary' className='flex-grow-1 button-medium' onClick={() => setModalMode('view')}>
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  className='flex-grow-1 button-medium'
                  onClick={handleSaveFilter}
                  loading={createFilterLoading || updateFilterLoading}
                  disabled={name.length < _MIN_NAME_LENGTH}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
